import React from "react";
import { Link } from "react-router-dom";
import siteConfig, { formatCurrency } from "../config/siteConfig";
import "../css/about.scss";
import "../css/delivery.scss";

const deliveryMethods = [
  {
    name: "Tracked standard delivery",
    timing: "Usually 3–7 business days after dispatch",
    detail: "The available service, destination, final charge, and current estimate are shown at checkout.",
  },
];

function Delivery() {
  return (
    <main className="editorial-page policy-page delivery-page">
      <div className="editorial-page__inner">
        <header className="editorial-page__hero">
          <p className="editorial-page__eyebrow">Customer care</p>
          <h1>Shipping information</h1>
          <p className="editorial-page__lede">
            From our shelves to your desk, we pack each order carefully and keep
            the journey clear.
          </p>
          <p className="policy-updated">
            Last updated <time dateTime="2026-10-19">October 19, 2026</time>
          </p>
        </header>

        <nav className="policy-nav" aria-label="Shipping topics">
          <a href="#processing">Processing</a>
          <a href="#delivery-methods">Delivery times</a>
          <a href="#tracking">Tracking</a>
          <a href="#destinations">Destinations</a>
          <a href="#customs">Customs</a>
          <a href="#problems">Delivery problems</a>
        </nav>

        <section className="policy-section" id="processing">
          <p className="editorial-page__section-label">Before dispatch</p>
          <h2>Processing your order</h2>
          <p>
            In-stock orders are normally prepared within one to two business
            days. Orders placed after the daily fulfilment cut-off, on weekends,
            or on a Hong Kong public holiday begin processing the next business day.
          </p>
          <p>
            Processing can take longer during promotions, public holidays,
            product-safety checks, or when address or payment verification is
            required. If we split an order, we will send a dispatch notice for
            each parcel and will not add a shipping fee after checkout.
          </p>
        </section>

        <section className="policy-section" id="delivery-methods">
          <p className="editorial-page__section-label">Estimated transit</p>
          <h2>Delivery options</h2>
          <p>
            Delivery estimates begin when a parcel leaves our studio, not when
            the order is placed. They are estimates rather than guarantees.
            Customs, carrier disruption, severe weather, remote destinations,
            and an incomplete address can add time.
          </p>
          <div className="shipping-methods" role="list">
            {deliveryMethods.map((method) => (
              <article
                className="shipping-method"
                role="listitem"
                key={method.name}
              >
                <h3>{method.name}</h3>
                <strong>{method.timing}</strong>
                <p>{method.detail}</p>
              </article>
            ))}
          </div>
          <p className="policy-callout">
            The current tracked standard delivery charge is{" "}
            <strong>{formatCurrency(siteConfig.flatShippingRate)}</strong> per order.
            The amount displayed at checkout controls if a later rate change is
            made before you order.
          </p>
        </section>

        <section className="policy-section" id="tracking">
          <p className="editorial-page__section-label">After dispatch</p>
          <h2>Tracking and address changes</h2>
          <ul className="policy-list">
            <li>A tracking link is included in the dispatch email.</li>
            <li>
              Tracking can take up to 24 hours to show movement after the label is
              created.
            </li>
            <li>
              Contact us promptly if an address needs correcting. Once a carrier
              accepts a parcel, changes are not guaranteed.
            </li>
            <li>
              If tracking says delivered but the parcel is missing, check safe
              places, household members, building staff, and the carrier before
              contacting us promptly.
            </li>
          </ul>
        </section>

        <section className="policy-section" id="destinations">
          <p className="editorial-page__section-label">Where we ship</p>
          <h2>Destinations and special addresses</h2>
          <p>
            We dispatch from Hong Kong to the destinations shown at checkout.
            We do not accept orders for destinations restricted by applicable
            sanctions or carrier limitations. Import duties or local taxes, when
            applicable, are the recipient&apos;s responsibility unless checkout states otherwise.
          </p>
          <p>
            Standard service can generally reach P.O. boxes. Express services may
            require a street address. Oversized or regulated art materials may
            have additional destination restrictions.
          </p>
        </section>

        <section className="policy-section" id="customs">
          <p className="editorial-page__section-label">Cross-border orders</p>
          <h2>Customs, duties, and import requirements</h2>
          <p>
            Customs may inspect or delay a parcel. Unless checkout expressly says
            duties and taxes are included, the recipient is responsible for
            import duties, local taxes, brokerage, permits, and other destination
            charges. We cannot mark a commercial order as a gift or declare an
            inaccurate value.
          </p>
          <p>
            If a parcel is returned because charges were not paid, import was
            refused, or required information was not supplied, we may refund the
            returned goods after receipt less outward delivery, carrier return
            fees, and non-refundable charges where applicable law permits.
          </p>
        </section>

        <section className="policy-section" id="problems">
          <p className="editorial-page__section-label">When delivery goes wrong</p>
          <h2>Delayed, lost, damaged, and undeliverable parcels</h2>
          <p>
            Contact us promptly if tracking has not moved beyond the carrier&apos;s
            expected window, a parcel arrives damaged, or the contents are
            incomplete. Keep the parcel, labels, and packaging and provide clear
            photographs when requested. We may need to complete a carrier inquiry
            before issuing the appropriate replacement or refund.
          </p>
          <p>
            When a parcel is returned for an incorrect or incomplete customer-
            supplied address, failed collection, or refused delivery, any
            redelivery or lawful deduction for unrecoverable carrier fees will be
            explained first. Nothing here limits a mandatory remedy for loss or
            damage occurring while the parcel remained our legal responsibility.
          </p>
        </section>

        <aside className="policy-demo-note" aria-label="Shipping help">
          <h2>Need delivery help?</h2>
          <p>
            For address changes, tracking questions, or destination availability,
            please <Link to="/contact">contact us</Link> before dispatch.
          </p>
        </aside>
        <p className="policy-back-link"><Link to="/policies">View all store policies</Link></p>
      </div>
    </main>
  );
}

export default Delivery;
